import { Close, Save, Settings } from "@mui/icons-material";
import { Button } from "@mui/material";
import { Box } from "@mui/system";
import { App, View } from "@pimo/pimo-app-builder";
import {
  ChipCell,
  DateCell,
  DerivePMTableProps,
  DragAndDropLayout,
  generateTableHeaderEntriesWithSorting,
  PMTableEventNames,
  PMTableEventPayload,
  PMTableSortEventPayload,
  ProgressBarCell,
  SortDirection,
  TextCardCell,
} from "@pimo/pimo-components";
import PMTable from "@pimo/pimo-components/src/lib/pm-components/pm-table/pm-table";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import { ResourceLockPlugin } from "@pimo/resource-lock-plugin";
import { generatePath } from "react-router-dom";
import { Configuration, ReportingItem } from "vmd-types";
import { canUserPerformOperation, getContrastColor } from "vmd-utils";

import { TestAppState } from "../../app";
import { BadgeCell } from "../../components/badge-cell";
import { DcRelevanceCell } from "../../components/dc-relevance-cell";
import { DynamicTablePagination } from "../../components/dynamic-table/dynamic-table-component";
import { OpenAssessmentCell } from "../../components/open-assessment-cell";
import { SearchAndFilter } from "../../components/project-filter-dialog";
import { ReportTableActionsMenu } from "../../components/report-table-actions-menu";
import {
  APP_ROUTES,
  CHART_ACCENT_BLUE,
  CRITICALITY_TEXT_COLOR,
  DONUT_CHART_COLORS,
  getReviewStatusBadgeProps,
  RECORD_TYPE_BADGE_COLORS,
  SECONDARY_TEXT_COLOR,
} from "../../constants";
import { createDynamicTableState } from "../../helpers/dynamic-table";
import { downloadBusinessAppExport } from "../../helpers/fetch-business-app-export";
import {
  beginTableConfigurationEdit,
  markTableConfigurationDirty,
  markTableConfigurationDirtyFromColumnPayload,
  restorePersistedTableConfiguration,
  setTableConfigurationConfirmationPopup,
} from "../../helpers/table-configuration-state";
import { formatDate } from "../../utils/format-date";
import { getItemCreationDisplayName } from "../../utils/get-item-creation-display-name";
import {
  getLlmSuggestedAnswerValues,
  getLlmSuggestedDisplayValue,
  getLlmSuggestedQuestions,
} from "../../utils/get-llm-suggested-display-value";
import { getLockedByMessage } from "../../utils/get-locked-by-message";
import { getRecordTypeBadgeConfig } from "../../utils/get-record-type-badge-config";
import { getStatusBadgeColumnWidth } from "../../utils/get-status-badge-column-width";
import {
  ReportingItemSortableFields,
  resolveStatusOrder,
  sortReportingItems,
} from "../../utils/sort-table-data";
import {
  ADOIT_ID_FIELD_NAME,
  buildReportTableCustomFieldValueMaps,
  deriveReportTableColumnVisibility,
  doesItemPassFilterCriteria,
  EUC_FIELD_NAME,
  findFormQuestionIdByFieldName,
  getActiveReportBusinessAppFilterLabels,
  getDcRelevanceDisplay,
  getDcSystemsFilterData,
  getEucDisplayValue,
  getFormAnswerValues,
  getRecordSource,
  getReportBusinessAppExportItemIds,
  getSystemTypeHeaderLabel,
  REALIZES_DOMAINS_AGGREGATED_FIELD_NAME,
  REPORT_MENU_COLUMN_WIDTH_PX,
  REPORT_OPEN_COLUMN_WIDTH_PX,
  REPORT_TABLE_FIELD_LOCALE_KEYS,
  REPORTING_SYSTEM_OVERVIEW_TABLE_FIELD_ORDER,
  resolveReportTableColumnOrder,
  resolveReportTableCriticalityBadgeColor,
  resolveReportTableDistributionChannelCellText,
  resolveReportTableDropdownFieldOneColor,
  resolveReportTableStatusCell,
} from "../report-plugin";

/**
 * Adds the tracking items table component to the view: the ~1,400-line
 * block carved out of `ReportPlugin.addComponentsToLayout` (#2222). It owns
 * the table's column definitions, cell renderers, sort/filter state, and
 * the column-configuration (add/reorder/delete) edit flow.
 *
 * Kept as a single function rather than split further for now — the
 * column-config state, filtering, and rendering are tightly interdependent
 * within one `mapState` callback, unlike the independent cards in
 * `./cards/*`.
 */
export function addTrackingItemsTable(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>,
  language: string,
  configuration: Configuration | undefined
) {
  const enabledItemCustomFieldsForTable =
    configuration?.customFields?.filter(
      (field) =>
        field?.enabled &&
        field?.flag !== "delete" &&
        (field?.purpose === "itemBasic" || field?.purpose === "itemReporting")
    ) ?? [];

  const baseTrackingItemsTableDefinition = [
    { component: TextCardCell },
    { component: TextCardCell },
    { component: BadgeCell },
    { component: TextCardCell },
    { component: TextCardCell },
    { component: BadgeCell },
    { component: ChipCell },
    { component: TextCardCell },
    { component: TextCardCell },
    { component: TextCardCell },
    { component: BadgeCell },
    { component: BadgeCell },
    { component: OpenAssessmentCell },
  ];

  const trackingItemsTableDefinition: Array<{
    component:
      | typeof TextCardCell
      | typeof ChipCell
      | typeof BadgeCell
      | typeof DcRelevanceCell
      | typeof ProgressBarCell
      | typeof DateCell
      | typeof OpenAssessmentCell;
  }> = [
    ...baseTrackingItemsTableDefinition,
    ...enabledItemCustomFieldsForTable.map((field) => ({
      component:
        field.type === "Date"
          ? DateCell
          : field.type === "Select"
            ? BadgeCell
            : TextCardCell,
    })),
  ];

  const trackingItemsTable = new PMTable<typeof trackingItemsTableDefinition>(
    trackingItemsTableDefinition
  );

  const trackingItemsTableComponent = view.addComponent<
    DerivePMTableProps<typeof trackingItemsTable>,
    PMTableEventNames,
    PMTableEventPayload
  >({
    component: trackingItemsTable,
    layoutProps: {
      componentId: "trackingItemsTableComponent",
      labels: ["table", "tracking items"],
      description:
        getLocalizedMessage(language, "showsTableViewTrackingItems") ||
        "Shows a table view of the tracking items of this project.",
      title:
        getLocalizedMessage(language, "projectOverview") || "Project Overview",
    },
  });

  trackingItemsTableComponent.on("sort", (event) => {
    if (!event?.payload || !app) {
      return;
    }

    const payload = event?.payload as PMTableSortEventPayload<
      keyof ReportingItemSortableFields
    >;
    const state = app.getAppState();
    let direction: SortDirection = "ASC";

    // Swap direction if key is already being sorted by in ascending order.
    if (
      state.reportPluginSortData?.key === payload?.sort?.key &&
      state.reportPluginSortData?.direction === "ASC"
    ) {
      direction = "DESC";
    }

    app.patchAppState({
      reportPluginSortData: {
        key: payload?.sort?.key,
        direction,
      },
    });
  });

  trackingItemsTableComponent.mapState(
    ({
      currentReport,
      settings,
      reportPluginSortData,
      reportPluginColumnOrder,
      reportTablePagination,
      userProfile,
      isSidebarOpen,
      language,
      filterData,
      reportTableConfiguration,
      lockDetails,
    }) => {
      const resourceLockPlugin =
        app?.getPluginByName<ResourceLockPlugin<TestAppState>>(
          "ResourceLockPlugin"
        );

      // Needed up-front so sortReportingItems can sort recordSource,
      // realizesDomainsAggregated and degreeOfCompletion by their actual
      // displayed values instead of leaving those columns unsorted.
      const realizesDomainsAggregatedQuestionId = findFormQuestionIdByFieldName(
        settings?.configuration,
        REALIZES_DOMAINS_AGGREGATED_FIELD_NAME
      );
      const adoitIdQuestionId = findFormQuestionIdByFieldName(
        settings?.configuration,
        ADOIT_ID_FIELD_NAME
      );
      const llmSuggestedQuestions = getLlmSuggestedQuestions(
        settings?.configuration
      );

      const reportingItems = sortReportingItems(
        (currentReport?.project?.reportingItems ?? [])
          .filter((i) => !i.isChannelHeader)
          .map((i) => ({
            ...i,
            customId: i.basicItem?.customId,
            recordSource: getRecordSource(i, adoitIdQuestionId),
            realizesDomainsAggregated: getFormAnswerValues(
              i,
              realizesDomainsAggregatedQuestionId
            ).join(", "),
            dcRelevance: getDcRelevanceDisplay(i).value,
            llmSuggested: getLlmSuggestedDisplayValue(
              getLlmSuggestedAnswerValues(
                i,
                llmSuggestedQuestions,
                settings?.configuration
              )
            ),
            _degreeOfCompletionOrder: resolveStatusOrder(
              settings?.configuration?.degreeOfCompletionDefinition,
              i.degreeOfCompletion
            ),
          })) as unknown as ReportingItem[],
        reportPluginSortData
      ) as ReportingItem[];

      const dynamicTableState = createDynamicTableState({
        app: app,
        paginationPrefix: "report",
        persistedPagination: reportTablePagination,
        onPatchPagination: (pagination) => {
          app?.patchAppState({
            reportTablePagination: pagination,
          });
        },
        items: reportingItems,
        filterFn: (item) =>
          doesItemPassFilterCriteria(item, filterData, settings),
      });

      const filteredReportingItems = dynamicTableState.filteredItems;
      const paginatedReportingItems = dynamicTableState.visibleItems;
      const paginationController = dynamicTableState.paginationController;

      // Size the Review Status column to the labels actually on screen, so it hugs Open when there's no long badge like "Ready to Submit".
      const degreeColumnWidthPx = getStatusBadgeColumnWidth(
        paginatedReportingItems.map((item) => item.degreeOfCompletion)
      );

      const ownerFilterOptions = [
        ...new Set(
          reportingItems
            .map((item) => item.basicItem?.owner?.trim())
            .filter((value): value is string => Boolean(value))
        ),
      ].sort((a, b) => a.localeCompare(b));

      const distributionChannelFilterOptions = [
        ...new Set(
          reportingItems
            .flatMap((item) => item.distributionChannel ?? [])
            .map(
              (channelId) =>
                settings?.configuration?.distributionChannelDefinition?.find(
                  (channel) => channel.id === channelId
                )?.label ?? channelId
            )
            .map((label) => label?.trim())
            .filter((value): value is string => Boolean(value))
        ),
      ].sort((a, b) => a.localeCompare(b));

      const lifeCycleFilterOptions = [
        ...new Set(
          reportingItems
            .map((item) => item.basicItem?.lifeCycle?.trim())
            .filter((value): value is string => Boolean(value))
        ),
      ].sort((a, b) => a.localeCompare(b));

      const criticalityFilterOptions = [
        ...new Set(
          reportingItems
            .map((item) => item.basicItem?.criticality?.trim())
            .filter((value): value is string => Boolean(value))
        ),
      ].sort((a, b) => a.localeCompare(b));

      const reviewStatusFilterOptions = [
        ...new Set(
          reportingItems
            .map((item) => item.degreeOfCompletion?.trim())
            .filter((value): value is string => Boolean(value))
        ),
      ].sort((a, b) => a.localeCompare(b));

      const eucQuestionId = findFormQuestionIdByFieldName(
        settings?.configuration,
        EUC_FIELD_NAME
      );

      const realizesDomainsAggregatedFilterOptions = [
        ...new Set(
          reportingItems.flatMap((item) =>
            getFormAnswerValues(item, realizesDomainsAggregatedQuestionId)
          )
        ),
      ].sort((a, b) => a.localeCompare(b));

      const eucFilterOptions = [
        ...new Set(
          reportingItems
            .flatMap((item) => getFormAnswerValues(item, eucQuestionId))
            .map((value) => getEucDisplayValue(value))
            .filter((value): value is "Yes" | "No" => value !== undefined)
        ),
      ].sort((a, b) => a.localeCompare(b));

      // Ensure the returned object matches DerivePMTableProps type
      // and that columnProps is a tuple of the correct length and types
      // const reportingDetails = settings?.reportingDetails;
      const rawTableHeaderEntries =
        reportTableConfiguration?.tableHeaderEntries ?? [];

      const toCustomColumnFieldName = (label?: string) => {
        const normalizedLabel = (label ?? "")
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");

        return `customField-${normalizedLabel || "unnamed"}`;
      };

      const enabledItemCustomFields =
        settings?.configuration?.customFields?.filter(
          (field) =>
            field?.enabled &&
            field?.flag !== "delete" &&
            (field?.purpose === "itemBasic" ||
              field?.purpose === "itemReporting")
        ) ?? [];

      const customLabelByFieldName = new Map(
        enabledItemCustomFields.map((field) => [
          toCustomColumnFieldName(field.label),
          field.label,
        ])
      );

      const customFieldDefinitionByFieldName = new Map(
        enabledItemCustomFields.map((field) => [
          toCustomColumnFieldName(field.label),
          field,
        ])
      );

      const enabledCustomFieldNames = new Set(customLabelByFieldName.keys());

      const orderedUniqueHeaders = rawTableHeaderEntries.filter(
        (entry, index, source) =>
          source.findIndex((e) => e.fieldName === entry.fieldName) === index
      );

      const orderedUniqueKnownHeaders = orderedUniqueHeaders.filter((entry) =>
        REPORTING_SYSTEM_OVERVIEW_TABLE_FIELD_ORDER.includes(
          entry.fieldName as (typeof REPORTING_SYSTEM_OVERVIEW_TABLE_FIELD_ORDER)[number]
        )
      );

      const orderedUniqueCustomHeaders = orderedUniqueHeaders
        .filter(
          (entry) =>
            String(entry.fieldName).startsWith("customField-") &&
            enabledCustomFieldNames.has(String(entry.fieldName))
        )
        .map((entry) => ({
          ...entry,
          label:
            customLabelByFieldName.get(String(entry.fieldName)) ?? entry.label,
        }));

      const existingCustomFieldNames = new Set(
        orderedUniqueCustomHeaders.map((entry) => String(entry.fieldName))
      );

      const missingEnabledCustomHeaders = enabledItemCustomFields
        .map((field) => {
          const fieldName = toCustomColumnFieldName(field.label);
          if (existingCustomFieldNames.has(fieldName)) {
            return null;
          }

          return {
            fieldName,
            label: field.label,
            isSortable: true,
            isHidden: false,
            sortDirection: null,
          };
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry != null);

      const headerByField = new Map(
        orderedUniqueKnownHeaders.map((entry) => [entry.fieldName, entry])
      );

      const normalizedBaseHeaders =
        REPORTING_SYSTEM_OVERVIEW_TABLE_FIELD_ORDER.map((fieldName) => {
          const fromConfig = headerByField.get(fieldName);

          return (
            fromConfig ?? {
              fieldName,
              label:
                fieldName === "recordType"
                  ? getSystemTypeHeaderLabel(language)
                  : fieldName === "open"
                    ? getLocalizedMessage(language || "en", "open") || "Open"
                    : getLocalizedMessage(
                        language || "en",
                        REPORT_TABLE_FIELD_LOCALE_KEYS[fieldName]
                      ) || fieldName,
              isSortable: fieldName !== "open",
              isHidden: false,
              sortDirection: null,
            }
          );
        });

      const normalizedHeaders = [
        ...normalizedBaseHeaders,
        ...orderedUniqueCustomHeaders,
        ...missingEnabledCustomHeaders,
      ];

      const normalizedHeaderByFieldName = new Map(
        normalizedHeaders.map((entry) => [String(entry.fieldName), entry])
      );

      // Saved table configuration order is the source of truth.
      // Keep that order, then append any missing columns.
      const savedOrderedFieldNames = orderedUniqueHeaders
        .map((entry) => String(entry.fieldName))
        .filter((fieldName) => normalizedHeaderByFieldName.has(fieldName));

      const missingFieldNames = normalizedHeaders
        .map((entry) => String(entry.fieldName))
        .filter((fieldName) => !savedOrderedFieldNames.includes(fieldName));

      const orderedHeadersForRender = [
        ...savedOrderedFieldNames,
        ...missingFieldNames,
      ].map((fieldName) => normalizedHeaderByFieldName.get(fieldName)!);

      const tableHeaderEntries =
        generateTableHeaderEntriesWithSorting<ReportingItemSortableFields>({
          entries: orderedHeadersForRender.map((e) => ({
            ...e,
            fieldName: e.fieldName as keyof ReportingItemSortableFields,
            label:
              e.label ||
              (e.fieldName === "recordType"
                ? getSystemTypeHeaderLabel(language)
                : getLocalizedMessage(
                    language || "en",
                    REPORT_TABLE_FIELD_LOCALE_KEYS[
                      e.fieldName as (typeof REPORTING_SYSTEM_OVERVIEW_TABLE_FIELD_ORDER)[number]
                    ]
                  )) ||
              getLocalizedMessage(language || "en", `${e.fieldName}`),
          })),
          tableSortData: reportPluginSortData,
        });

      const hasVisibleOpenColumn = tableHeaderEntries.some(
        (entry) =>
          String(entry.fieldName) === "open" &&
          entry.isVisible !== false &&
          !entry.isHidden
      );

      // Row-actions ("...") menu is restricted to admins; OEs get no menu at all.
      const hasVisibleMenuColumn = canUserPerformOperation({
        userGroups: userProfile?.groups ?? [],
        lowestLevelAccessRequiredToPerformThisOperation: "admin",
        client: settings?.configuration?.client,
        tool: settings?.configuration?.tool,
      });

      const menuColumnReservePx = hasVisibleMenuColumn
        ? REPORT_MENU_COLUMN_WIDTH_PX
        : 0;
      // The menu lives inside the "open" cell, so it widens that column
      // instead of adding one. A column of its own would need a header cell
      // to get any width at all, PMTable renders the table `table-layout: fixed`.
      const openColumnWidthPx =
        REPORT_OPEN_COLUMN_WIDTH_PX + menuColumnReservePx;

      const tableHeaderEntriesWithVisibility =
        deriveReportTableColumnVisibility(tableHeaderEntries, {
          hasVisibleOpenColumn,
          openColumnWidthPx,
          degreeColumnWidthPx,
        });

      const { effectiveColumnOrder, needsSeeding } =
        resolveReportTableColumnOrder(
          tableHeaderEntriesWithVisibility.length,
          reportPluginColumnOrder
        );

      // Seed order once after refresh so drag/reorder state is restored.
      if (needsSeeding) {
        app?.patchAppState({
          reportPluginColumnOrder: effectiveColumnOrder,
        });
      }

      return {
        title: getLocalizedMessage(language, "overview") ?? "Overview",
        overflow: "visible",
        isSidebarOpen: isSidebarOpen,
        rightSlotOverride: (
          <Box
            sx={{
              p: 0,
              display: "flex",
              marginTop: "24px",
              marginLeft: "32px",
              flexDirection: "row",
              gap: 2,
              alignItems: "center",
              minWidth: 0,
              maxWidth: "100%",
              overflowX: { xs: "auto", lg: "visible" },
              overflowY: { xs: "hidden", lg: "visible" },
            }}
          >
            <SearchAndFilter
              filterData={getDcSystemsFilterData(filterData)}
              hideProminentInMoreFilters
              filterLabel={
                getLocalizedMessage(language || "en", "filter") || "Filter"
              }
              clearAllLabel={
                getLocalizedMessage(language || "en", "resetAllFilters") ||
                "Reset all filters"
              }
              moreFiltersLabel={
                getLocalizedMessage(language || "en", "moreFilters") ||
                "More filters"
              }
              handleChange={(newFilterData) => {
                paginationController?.resetToFirstPage();

                app?.patchAppState({
                  filterData: newFilterData,
                });
              }}
              clearAllFilters={() => {
                paginationController?.resetToFirstPage();

                app?.patchAppState({
                  filterData: undefined,
                });
              }}
              options={{
                owner: {
                  isEnabled: true,
                  options: ownerFilterOptions,
                  label:
                    getLocalizedMessage(language || "en", "owner") || "Owner",
                },
                distributionChannel: {
                  isEnabled: true,
                  options: distributionChannelFilterOptions,
                  label:
                    getLocalizedMessage(
                      language || "en",
                      "distributionChannel"
                    ) || "Distribution Channel",
                },
                lifeCycle: {
                  isEnabled: true,
                  options: lifeCycleFilterOptions,
                  label:
                    getLocalizedMessage(language || "en", "lifeCycleStatus") ||
                    "Life Cycle Status",
                },
                criticality: {
                  isEnabled: true,
                  options: criticalityFilterOptions,
                  label:
                    getLocalizedMessage(language || "en", "criticality") ||
                    "Criticality",
                },
                reviewStatus: {
                  isEnabled: true,
                  options: reviewStatusFilterOptions,
                  label:
                    getLocalizedMessage(language || "en", "Review Status") ||
                    "Review Status",
                },
                realizesDomainsAggregated: {
                  isEnabled: true,
                  options: realizesDomainsAggregatedFilterOptions,
                  label: "Realizes (Domains Aggregated)",
                },
                euc: {
                  isEnabled: true,
                  options: eucFilterOptions,
                  label: "EUC",
                },
                dcRelevanceModifiedImport: {
                  isEnabled: true,
                  options: ["Yes", "No"],
                  label: "Modified by ADOIT import",
                },
              }}
            ></SearchAndFilter>
            <ReportTableActionsMenu
              canCreate={canUserPerformOperation({
                userGroups: userProfile?.groups || [],
                operation: "edit",
                object: "item",
                entityId: currentReport?.project?.id,
                client: settings?.configuration?.client,
                tool: settings?.configuration?.tool,
              })}
              createLabel={
                getLocalizedMessage(language, "createButton") || "Create"
              }
              createTooltip={`${
                getLocalizedMessage(language, "addNew") || "Add New"
              } ${getItemCreationDisplayName(settings?.configuration?.itemName)}`}
              downloadLabel={
                getLocalizedMessage(language, "downloadButton") || "Download"
              }
              downloadTooltip={
                getLocalizedMessage(language, "downloadBusinessApps") ||
                "Download Business Apps"
              }
              onCreate={() => {
                app?.navigate(
                  generatePath(APP_ROUTES.trackingItemDialog, {
                    id: currentReport?.project?.id ?? "",
                    "tracking-item-id": "new-tracking-item",
                    viewname: "dashboard",
                  })
                );
              }}
              onDownload={() => {
                const itemIds = getReportBusinessAppExportItemIds(
                  reportingItems,
                  filterData,
                  settings
                );
                const activeFilterLabels =
                  getActiveReportBusinessAppFilterLabels(filterData);

                void downloadBusinessAppExport(
                  itemIds,
                  activeFilterLabels
                ).then((success) => {
                  if (success) {
                    return;
                  }

                  app?.patchAppState({
                    snackbar: {
                      message:
                        getLocalizedMessage(
                          language,
                          "downloadBusinessAppsError"
                        ) ||
                        "Failed to download Business Apps. Please try again.",
                      severity: "error",
                      open: true,
                      type: "snackbar",
                    },
                  });
                });
              }}
            />
            {canUserPerformOperation({
              userGroups: userProfile?.groups || [],
              lowestLevelAccessRequiredToPerformThisOperation: "admin",
              client: settings?.configuration?.client,
              tool: settings?.configuration?.tool,
            }) &&
              (app?.getAppState().reportTableConfiguration?.isEditMode ? (
                <Box
                  sx={{
                    display: "flex",
                    p: 0,
                    gap: 0,
                  }}
                >
                  <Button
                    sx={{
                      borderRadius: "100%",
                      width: "40px",
                      height: "40px",
                      minWidth: "40px",
                      padding: 0,
                    }}
                    onClick={async () => {
                      if (!app) {
                        return;
                      }

                      setTableConfigurationConfirmationPopup(
                        app,
                        "reportTableConfiguration",
                        "save"
                      );
                    }}
                  >
                    <Save sx={{ height: "20px", width: "20px" }} />
                  </Button>
                  <Button
                    sx={{
                      borderRadius: "100%",
                      width: "40px",
                      height: "40px",
                      minWidth: "40px",
                      padding: 0,
                    }}
                    onClick={async () => {
                      if (!app) {
                        return;
                      }

                      const reportTableConfiguration =
                        app.getAppState().reportTableConfiguration;

                      if (!reportTableConfiguration) {
                        return;
                      }

                      if (reportTableConfiguration.isDirty) {
                        setTableConfigurationConfirmationPopup(
                          app,
                          "reportTableConfiguration",
                          "abort"
                        );

                        return;
                      }

                      await restorePersistedTableConfiguration({
                        app: app,
                        configKey: "reportTableConfiguration",
                        columnOrderKey: "reportPluginColumnOrder",
                        componentId: "trackingItemsTableComponent",
                      });
                    }}
                  >
                    <Close sx={{ height: "20px", width: "20px" }} />
                  </Button>
                </Box>
              ) : (
                <Button
                  sx={{
                    borderRadius: "100%",
                    width: "40px",
                    height: "40px",
                    minWidth: "40px",
                    padding: 0,
                  }}
                  onClick={() => {
                    if (!app) {
                      return;
                    }

                    beginTableConfigurationEdit(
                      app,
                      "reportTableConfiguration",
                      tableHeaderEntries
                    );
                  }}
                >
                  <Settings sx={{ height: "20px", width: "20px" }} />
                </Button>
              ))}
          </Box>
        ),
        showInfoIcon: false,
        showMenuIcon: false,
        allowColumnReorder: true,
        bottomRightSlotOverride: (
          <DynamicTablePagination
            paginationController={paginationController}
            totalItems={filteredReportingItems.length}
          />
        ),
        colOrder: effectiveColumnOrder,
        language: language,
        isEditMode: reportTableConfiguration?.isEditMode,
        setColOrder: (order) =>
          app?.patchAppState({ reportPluginColumnOrder: order }),
        data: paginatedReportingItems.map((item) => {
          const itemLock =
            lockDetails?.itemLocks?.[`item-${item.basicItem?.id}`];
          const isLocked =
            itemLock?.isLocked && itemLock.lockedBy !== userProfile?.email;

          const itemCustomFields = (item.customFields ?? []) as {
            value?: unknown;
            customFieldDefinition?: {
              label?: string;
            };
          }[];

          const { customFieldValueByFieldName, customFieldValueByLabel } =
            buildReportTableCustomFieldValueMaps(
              itemCustomFields,
              toCustomColumnFieldName
            );

          const recordTypeBadge = getRecordTypeBadgeConfig(
            item.basicItem?.recordType
          );

          const tooltipCell = {
            autoCutBody: true,
            component: TextCardCell,
            cardProps: {
              sx: {
                zIndex: 3,
                backgroundColor: "background.paper",
              },
            },

            tooltipProps: {
              placement: "right",
              slotProps: {
                tooltip: {
                  sx: {
                    backgroundColor: "#fff",
                    color: SECONDARY_TEXT_COLOR,
                    border: "1px solid",
                    borderColor: "background.paper",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                    fontSize: "14px",
                    fontWeight: 400,
                    padding: "12px 16px",
                    borderRadius: 4,
                  },
                },
              },
            },
          };

          const openAssessmentDialog = async () => {
            const reportingItem = item;
            const basicItem = reportingItem?.basicItem;

            if (!basicItem || isLocked) {
              return;
            }

            try {
              await resourceLockPlugin?.lock(`item-${basicItem.id}`);
            } catch {
              // Item is already locked by someone else; don't navigate into the edit dialog.
              return;
            }

            app?.patchAppState({
              editForm: {
                currentTrackingItem: {
                  ...basicItem,
                  distributionChannel: reportingItem?.distributionChannel,
                },
              },
            });
            app?.navigate(
              generatePath(APP_ROUTES.trackingItemDialog, {
                id: currentReport?.project?.id ?? "",
                "tracking-item-id": basicItem?.id ?? "",
                viewname:
                  getLocalizedMessage(language, "dashboard") || "dashboard",
              })
            );
          };

          const baseCellByFieldName = new Map<string, Record<string, unknown>>([
            [
              "customId",
              {
                component: TextCardCell,
                body: item.basicItem?.customId?.toString() ?? "",
                cardProps: {
                  sx: {
                    position: "sticky" as const,
                    left: 0,
                    zIndex: 3,
                    maxWidth: 120,
                    backgroundColor: "background.paper",
                  },
                },
                bodyProps: {
                  sx: {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                },
              },
            ],
            [
              "recordSource",
              {
                component: TextCardCell,
                body: getRecordSource(item, adoitIdQuestionId),
              },
            ],
            [
              "llmSuggested",
              {
                component: TextCardCell,
                body:
                  (item as unknown as { llmSuggested?: string }).llmSuggested ??
                  "No",
              },
            ],
            [
              "name",
              {
                ...tooltipCell,
                body: item.basicItem?.name ?? "",
              },
            ],
            [
              "recordType",
              {
                component: BadgeCell,
                body: recordTypeBadge.label,
                color: recordTypeBadge.backgroundColor,
                textColor: getContrastColor(recordTypeBadge.backgroundColor),
              },
            ],
            [
              "owner",
              {
                component: TextCardCell,
                body: item.basicItem?.owner ?? "n/a",
              },
            ],
            [
              "distributionChannel",
              {
                component: TextCardCell,
                body: resolveReportTableDistributionChannelCellText(
                  item.distributionChannel,
                  settings?.configuration?.distributionChannelDefinition
                ),
              },
            ],
            [
              "lifeCycle",
              {
                component: BadgeCell,
                body: item.basicItem?.lifeCycle ?? "n/a",
                color: RECORD_TYPE_BADGE_COLORS.fallback,
                textColor: getContrastColor(RECORD_TYPE_BADGE_COLORS.fallback),
              },
            ],
            [
              "criticality",
              {
                component: BadgeCell,
                body: item.basicItem?.criticality ?? "n/a",
                textColor: CRITICALITY_TEXT_COLOR,
                color: resolveReportTableCriticalityBadgeColor(
                  item.basicItem?.criticality
                ),
              },
            ],
            [
              "processSteps",
              {
                ...tooltipCell,
                body:
                  item.basicItem?.processSteps?.filter(Boolean).join(", ") ||
                  "n/a",
              },
            ],
            [
              "applicationCapabilities",
              {
                ...tooltipCell,
                body:
                  item.basicItem?.applicationCapabilities
                    ?.filter(Boolean)
                    .join(", ") || "n/a",
              },
            ],
            [
              "realizesDomainsAggregated",
              {
                ...tooltipCell,
                body:
                  getFormAnswerValues(
                    item,
                    realizesDomainsAggregatedQuestionId
                  ).join(", ") || "n/a",
              },
            ],
            [
              "technicalExposure",
              {
                ...tooltipCell,
                component: TextCardCell,
                body:
                  item.basicItem?.technicalExposure
                    ?.filter(Boolean)
                    .join(", ") || "n/a",
              },
            ],
            [
              "dcRelevance",
              (() => {
                const dcRelevance = getDcRelevanceDisplay(item);

                return {
                  component: DcRelevanceCell,
                  body: dcRelevance.value,
                };
              })(),
            ],
            [
              "dropdownFieldOne",
              (() => {
                const dropdownFieldOneColor =
                  resolveReportTableDropdownFieldOneColor(
                    item.basicItem?.dropdownFieldOne,
                    settings?.configuration,
                    DONUT_CHART_COLORS[0]
                  );

                return {
                  component: BadgeCell,
                  body: item.basicItem?.dropdownFieldOne ?? "",
                  color: dropdownFieldOneColor,
                  textColor: getContrastColor(dropdownFieldOneColor),
                };
              })(),
            ],
            [
              "status",
              {
                component: BadgeCell,
                ...resolveReportTableStatusCell(item, settings?.configuration),
              },
            ],
            [
              "degreeOfCompletion",
              {
                component: BadgeCell,
                body: item.degreeOfCompletion ?? "",
                ...getReviewStatusBadgeProps(
                  item.degreeOfCompletion,
                  settings?.configuration
                ),
                cardProps: {
                  sx: {
                    position: "sticky" as const,
                    right: `${hasVisibleOpenColumn ? openColumnWidthPx : 0}px`,
                    zIndex: 4,
                    minWidth: degreeColumnWidthPx,
                    width: degreeColumnWidthPx,
                    backgroundColor: "background.paper",
                  },
                },
              },
            ],
            [
              "open",
              {
                component: OpenAssessmentCell,
                onClick: openAssessmentDialog,
                isLocked: isLocked ?? false,
                tooltip: getLockedByMessage("item", language, itemLock),
                deleteLabel:
                  getLocalizedMessage(language || "en", "delete") || "Delete",
                onDelete: hasVisibleMenuColumn
                  ? () => {
                      const basicItem = item?.basicItem;
                      if (!basicItem) return;

                      // set flags in app state so the ConfirmationDialog can show
                      app?.patchAppState({
                        showDeleteTrackingItemDialog: true,
                        pendingDeleteTrackingItemId: basicItem.id,
                      });
                    }
                  : undefined,
                cardProps: {
                  sx: {
                    position: "sticky" as const,
                    right: 0,
                    zIndex: 4,
                    minWidth: openColumnWidthPx,
                    width: openColumnWidthPx,
                    backgroundColor: "background.paper",
                  },
                },
              },
            ],
            [
              "progress",
              {
                component: ProgressBarCell,
                header: `${Math.round(item.progress ?? 0)} %`,
                progress: item.progress ?? 0,
                progressColor: CHART_ACCENT_BLUE,
              },
            ],
            [
              "budget",
              {
                component: ProgressBarCell,
                header: "",
                progress: item?.basicItem?.budget ?? 0,
                progressColor: "lightgrey",
              },
            ],
            [
              "startDate",
              {
                component: DateCell,
                date: item.basicItem?.startDate
                  ? formatDate(item.basicItem?.startDate)
                  : "N/A",
              },
            ],
            [
              "endDate",
              {
                component: DateCell,
                date: item.basicItem?.endDate
                  ? formatDate(item.basicItem?.endDate)
                  : "N/A",
              },
            ],
          ]);

          const orderedColumnProps = tableHeaderEntriesWithVisibility.map(
            (header) => {
              const fieldName = String(header.fieldName);

              if (fieldName.startsWith("customField-")) {
                const customFieldDefinition =
                  customFieldDefinitionByFieldName.get(fieldName);

                const rawValue =
                  customFieldValueByFieldName.get(fieldName) ??
                  customFieldValueByLabel.get(
                    String(header.label ?? "")
                      .trim()
                      .toLowerCase()
                  );

                const baseState = {
                  isHidden: header.isHidden,
                  isVisible: header.isVisible,
                };

                if (customFieldDefinition?.type === "Date") {
                  return {
                    component: DateCell,
                    date: rawValue ? formatDate(String(rawValue)) : "N/A",
                    ...baseState,
                  };
                }

                if (customFieldDefinition?.type === "Select") {
                  const optionColor =
                    customFieldDefinition.options?.find(
                      (option) => option.name === String(rawValue ?? "")
                    )?.color ?? RECORD_TYPE_BADGE_COLORS.itService;

                  return {
                    component: BadgeCell,
                    body: String(rawValue ?? "n/a"),
                    color: optionColor,
                    textColor: getContrastColor(optionColor),
                    ...baseState,
                  };
                }

                return {
                  component: TextCardCell,
                  body: String(rawValue ?? "n/a"),
                  cutBodyAfter:
                    customFieldDefinition?.type === "Text" ? 51 : undefined,
                  ...baseState,
                };
              }

              const baseCell = baseCellByFieldName.get(fieldName);

              return {
                ...(baseCell ?? {
                  component: TextCardCell,
                  body: "n/a",
                }),
                isHidden: header.isHidden,
                isVisible: header.isVisible,
              };
            }
          );

          return {
            columnProps: orderedColumnProps as any, // eslint-disable-line
            rowProps: {
              cardProps: {
                sx: { cursor: isLocked ? "not-allowed" : "pointer" },
              },
              // Row click opens the same form as the Open button (details modal hidden per VMD request).
              onClick: openAssessmentDialog,
              // No `onMenuOpen`: its menu always renders an "Edit" entry.
              // The delete-only menu is rendered by the "open" cell instead.
            },
          };
        }),
        tableHeaderEntries: tableHeaderEntriesWithVisibility,
      };
    }
  );

  trackingItemsTableComponent.on("reorderColumns", () => {
    if (!app) {
      return;
    }

    markTableConfigurationDirty(app, "reportTableConfiguration");
  });

  trackingItemsTableComponent.on("addColumn", (event) => {
    if (!event?.payload || !app) {
      return;
    }

    markTableConfigurationDirtyFromColumnPayload(
      app,
      "reportTableConfiguration",
      event.payload
    );
  });

  trackingItemsTableComponent.on("deleteColumn", (event) => {
    if (!event?.payload || !app) {
      return;
    }

    markTableConfigurationDirtyFromColumnPayload(
      app,
      "reportTableConfiguration",
      event.payload
    );
  });
}
